import React from 'react'
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { Form as FormikForm, Formik } from 'formik'
import { get, clamp } from 'lodash'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartPlus, faSpinner } from '@fortawesome/free-solid-svg-icons'

import { OfferType } from '@monorepo/interfaces'
import { App } from '@monorepo/hooks'
import { Currency, LightBox } from '@monorepo/components/common'
import { FormGroup } from 'App/Components'
import { useItemImages } from 'App/hooks'

type OrderFormPropsType = {
  item: OfferType
  addHandler(meta: any): void
  onClose?(): void
}

export const OrderForm: React.FC<OrderFormPropsType> = ({
  item,
  addHandler,
  onClose,
}) => {
  const { currentContract } = App.useContract()

  const { images, thumbs } = useItemImages(item)

  return (
    <Formik
      initialValues={{
        amount: item.multiplicityAmount,
        contract: currentContract,
        reference: '',
      }}
      onSubmit={async (values) => {
        try {
          await addHandler(values)
          onClose?.()
        } catch (error) {
          const errors = get(error, 'error.response.data.result.errors')

          if (Array.isArray(errors) && errors.length > 0) {
            toast.warn(
              <>
                <strong>Ошибки:</strong>
                <ul>
                  {errors.map((error, idx) => (
                    <li key={idx}>{error.message}</li>
                  ))}
                </ul>
              </>
            )
          } else {
            toast.error('При добавлении возникла непредвиденная ошибка.')
          }
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit, isSubmitting }) => (
        <Form as={FormikForm}>
          <Modal.Body>
            {images && (
              <LightBox images={images}>
                <section
                  className="card d-flex flex-row mb-4"
                  style={{
                    minWidth: '100%',
                    overflowX: 'auto',
                  }}
                >
                  {thumbs?.map((thumb, idx) => (
                    <div key={idx} className="card--content m-2">
                      <img
                        src={thumb}
                        alt=""
                        style={{
                          objectFit: 'cover',
                          height: '25vh',
                          maxHeight: 100,
                          minHeight: 50,
                        }}
                      />
                    </div>
                  ))}
                </section>
              </LightBox>
            )}
            <Row>
              <Col>
                {item.quality?.description && (
                  <Alert variant="warning" className="w-100">
                    {item.quality?.description}
                  </Alert>
                )}
              </Col>
            </Row>
            <Row>
              <Form.Group as={Col} sm="6" xs="12" className="text-center">
                {/* TODO: Replace to NumberInput */}
                <FormGroup name="amount" label="Количество">
                  {({ field }: any) => {
                    const updateValue = (changeValue: number) =>
                      setFieldValue(
                        'amount',
                        clamp(
                          field.value + changeValue * item.multiplicityAmount,
                          item.minAmount,
                          item.amount
                        )
                      )

                    return (
                      <div className="input-number">
                        <input
                          className="form-control input-number__input"
                          type="number"
                          {...field}
                        />
                        <div
                          className="input-number__add"
                          onClick={updateValue.bind(null, 1)}
                        />
                        <div
                          className="input-number__sub"
                          onClick={updateValue.bind(null, -1)}
                        />
                      </div>
                    )
                  }}
                </FormGroup>
              </Form.Group>
              <Form.Group as={Col} sm="3" xs="6" className="text-center">
                {item.multiplicityAmount !== values.amount && (
                  <>
                    <Form.Label>Цена</Form.Label>
                    <Form.Control as="div">
                      <Currency value={item.price} />
                    </Form.Control>
                  </>
                )}
              </Form.Group>
              <Form.Group as={Col} sm="3" xs="6" className="text-center">
                <Form.Label>Стоимость</Form.Label>
                <Form.Control as="div">
                  <Currency value={item.price * values.amount} />
                </Form.Control>
              </Form.Group>
              <hr />
              <Form.Group as={Col} xs="12">
                <FormGroup
                  name="reference"
                  label="Коментарий к позиции | Reference"
                >
                  <Form.Control />
                </FormGroup>
              </Form.Group>
            </Row>
            <Row>
              <Col>
                {item.isReturnAllowed ? (
                  <>
                    {item.returnPercent > 0 && (
                      <Alert variant="warning" className="w-100">
                        Возврат {item.returnPercent}%
                      </Alert>
                    )}
                    {Boolean(item.returnConditions) && (
                      <Alert variant="info" className="w-100">
                        {item.returnConditions}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert variant="danger" className="w-100">
                    Возврат запрещён
                  </Alert>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Отмена
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Добавить в корзину&nbsp;
              <FontAwesomeIcon
                fixedWidth
                icon={isSubmitting ? faSpinner : faCartPlus}
                spin={isSubmitting}
              />
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Formik>
  )
}
